import { api, ApiError } from './api';
import { PageResponse } from './ordersApi';

export interface SettlementResponse {
  id: string;
  settlementNumber: string;
  garageId: string;
  garageName: string;
  periodStart: string;
  periodEnd: string;
  orderCount: number;
  totalAmount: number;
  grossRevenue: number;
  commissionTotal: number;
  vatOnCommission: number;
  /**
   * #546 — the tax invoice's VAT (5 % of its own net) minus the sum of the per-order VAT figures
   * below. 0 whenever they agree; otherwise the named line that says where the fils went.
   */
  vatRoundingDifference: number;
  refundDeductions: number;
  carriedBalance: number;
  // Spec 053 Flow D2 — negative balance rolled in from the previous cycle / carried to the next one.
  carriedBalanceIn: number;
  carriedBalanceOut: number;
  netPayable: number;
  currency: string;
  status: string;
  settledByUserId: string | null;
  settledAt: string | null;
  /** Spec 069 — resolved names of the admins who settled / approved it (absent before PR C). */
  settledByName?: string | null;
  approvedByName?: string | null;
  notes: string | null;
  createdAt: string;
  // Spec 053 Flow A1 — issued when payment is recorded (PAID), never at approval.
  taxInvoiceNumber?: string | null;
  taxInvoiceIssuedAt?: string | null;
}

/** Spec 051 AC-22 — shared admin/partner contract for the commission tax invoice PDF. */
export interface TaxInvoiceUrlResponse {
  status: 'AVAILABLE' | 'GENERATING' | 'NOT_AVAILABLE';
  downloadUrl: string | null;
  invoiceNumber: string | null;
}

/**
 * Spec 053 Flow A2 — commission credit note (CCN-YYYY-NNNNN) issued against a settlement's INV-
 * when a refund lands after the commission invoice exists. Amounts are the credited commission
 * (net), its VAT (BR-3) and the gross; `capped` marks a note clipped to the invoice balance (BR-2).
 */
export interface CreditNoteResponse {
  ccnId: string;
  number: string;
  issuedAt: string;
  net: number;
  vat: number;
  gross: number;
  originalInvoiceNumber: string;
  refundId: string | null;
  refOrderId?: string | null;
  refOrderNumber?: string | null;
  capped?: boolean;
  status: 'PENDING_DOCUMENT' | 'ISSUED';
  downloadUrl?: string | null;
}

/** Presigned CCN PDF state — same shape family as the tax-invoice URL endpoint. */
export interface CreditNoteUrlResponse {
  status: 'AVAILABLE' | 'GENERATING';
  downloadUrl?: string | null;
  number: string;
}

export interface SettlementLineItem {
  orderId: string;
  orderNumber: string | null;
  orderDate: string;
  customerName: string | null;
  serviceSummary: string | null;
  grossAmount: number;
  commissionRate: number | null;
  commissionAmount: number | null;
  vatOnCommission: number | null;
  netAmount: number | null;
  isRefunded: boolean;
  refundAmount: number | null;
  refundReason: string | null;
  /** Spec 053 (#470, AC-9) — commission (+VAT) reversed on this line by a refund; 0 when none. */
  reversedCommission: number;
  reversedVat: number;
  /** Spec 067 — the Tabby fee (net of VAT) the garage bears on this order, and its VAT; 0 for card. */
  garageFeeDeduction?: number;
  garageFeeVat?: number;
  /** Spec 068 — the service fee recharged to the garage on this order (GARAGE-fault refund), and its VAT; 0 when none. */
  serviceFeeDeduction?: number | null;
  serviceFeeDeductionVat?: number | null;
}

export interface PaymentConfirmation {
  type: string;
  documentKey: string | null;
  reference: string | null;
  bankName: string | null;
  transferDate: string | null;
  transferAmount: number | null;
}

export interface SettlementDetailResponse extends SettlementResponse {
  /** Spec 067 — Σ signed adjustments in this settlement (credits +, debits −). */
  adjustmentsTotal?: number;
  /** Spec 067 — "Tabby fees" the garage bears (net of VAT, positive) and the VAT on them (positive). */
  gatewayFeeDeductions?: number;
  gatewayFeeVat?: number;
  /** Spec 068 — "Service fee recharges" deducted from the garage (positive) and the VAT on them (positive). */
  serviceFeeDeductions?: number | null;
  serviceFeeDeductionsVat?: number | null;
  lineItems: SettlementLineItem[];
  paymentConfirmation: PaymentConfirmation | null;
}

export interface SettlementConfigResponse {
  garageId: string;
  settlementCycle: string;
  commissionRate: number;
  settlementDay: number;
  effectiveFrom: string;
  isDefault: boolean;
  // Spec 053 BR-15 — per-garage hold window (3–30 days); null = global default (3).
  holdDays?: number | null;
  /** The global default hold currently in force — shown when holdDays is null. */
  defaultHoldDays: number;
  /** Spec 067 — the garage's own Tabby fee bearer; null = the global default. */
  tabbyFeeBearer?: TabbyFeeBearer | null;
  /** Spec 067 — read-only: the bearer new Tabby payments for this garage get now. */
  tabbyFeeBearerEffective?: TabbyFeeBearer;
  /** Spec 067 — the Runtime Config default currently in force. */
  defaultTabbyFeeBearer?: TabbyFeeBearer;
}

export type TabbyFeeBearer = 'GARAGE' | 'GARAGAT';

/** Spec 067 — Tabby-paid orders still inside the Tabby holding period; never part of a payout total. */
export interface AwaitingTabbySettlement {
  count: number;
  grossAmount: number;
  earliestEligibleAt: string | null;
}

export interface GarageSettlementSummary {
  garageId: string;
  awaitingTabbySettlement: AwaitingTabbySettlement;
  currency: string;
}

export interface UpdateSettlementConfigRequest {
  settlementCycle?: string;
  commissionRate?: number;
  settlementDay?: number;
  holdDays?: number | null;
  /** Spec 067 — GARAGE | GARAGAT, or null for the global default (same pattern as holdDays). */
  tabbyFeeBearer?: TabbyFeeBearer | null;
}

export interface SettlementListParams {
  garageId?: string;
  garageName?: string;
  status?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  size?: number;
}

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8080';

async function uploadMultipart(url: string, formData: FormData, token: string) {
  const response = await fetch(`${API_BASE}${url}`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` },
    body: formData,
  });
  if (!response.ok) {
    // GlobalExceptionHandler bodies are { error, message, status }; 422 TRANSFER_AMOUNT_MISMATCH
    // and 409 status guards put the human text in `message` — surface it, keep the code for callers.
    const err = await response.json().catch(() => ({}));
    throw new ApiError(err.message || err.error || `HTTP ${response.status}`, response.status);
  }
  return response.json();
}

export const settlementsApi = {
  listSettlements: (params: SettlementListParams, token: string) => {
    const query = new URLSearchParams();
    if (params.garageId) query.set('garageId', params.garageId);
    if (params.garageName) query.set('garageName', params.garageName);
    if (params.status) query.set('status', params.status);
    if (params.dateFrom) query.set('dateFrom', params.dateFrom);
    if (params.dateTo) query.set('dateTo', params.dateTo);
    if (params.page !== undefined) query.set('page', String(params.page));
    if (params.size !== undefined) query.set('size', String(params.size));
    const qs = query.toString();
    return api.get<PageResponse<SettlementResponse>>(
      `/api/admin/settlements${qs ? `?${qs}` : ''}`, token
    );
  },

  getSettlementDetail: (settlementId: string, token: string) =>
    api.get<SettlementDetailResponse>(`/api/admin/settlements/${settlementId}`, token),

  markSettlementPaid: (settlementId: string, formData: FormData, token: string) =>
    uploadMultipart(`/api/admin/settlements/${settlementId}/pay`, formData, token) as Promise<SettlementResponse>,

  getConfirmationUrl: (settlementId: string, token: string) =>
    api.get<{ url: string; expiresIn: number }>(
      `/api/admin/settlements/${settlementId}/confirmation-url`, token
    ),

  // Spec 051 AC-22 / Spec 053 A1 — commission tax invoice PDF (only exists once PAID).
  getTaxInvoiceUrl: (settlementId: string, token: string) =>
    api.get<TaxInvoiceUrlResponse>(`/api/admin/settlements/${settlementId}/tax-invoice-url`, token),

  // Spec 053 Flow A2 — credit notes issued against this settlement's commission invoice.
  listCreditNotes: (settlementId: string, token: string) =>
    api.get<CreditNoteResponse[]>(`/api/admin/settlements/${settlementId}/credit-notes`, token),

  // Presigned URLs expire; fetch on click. GENERATING = PDF not rendered yet (retry via scheduler).
  getCreditNoteUrl: (ccnId: string, token: string) =>
    api.get<CreditNoteUrlResponse>(`/api/admin/settlements/credit-notes/${ccnId}/url`, token),

  getGarageConfig: (garageId: string, token: string) =>
    api.get<SettlementConfigResponse>(`/api/admin/garages/${garageId}/settlement-config`, token),

  updateGarageConfig: (garageId: string, config: UpdateSettlementConfigRequest, token: string) =>
    api.put<SettlementConfigResponse>(`/api/admin/garages/${garageId}/settlement-config`, config, token),

  // Spec 067 — "Awaiting Tabby settlement" on the garage Settlement tab (not part of any payable total).
  getGarageSettlementSummary: (garageId: string, token: string) =>
    api.get<GarageSettlementSummary>(`/api/admin/garages/${garageId}/settlement-summary`, token),

  // Spec 049 — settlement lifecycle + workspace
  getCounts: (token: string) =>
    api.get<SettlementCounts>('/api/admin/settlements/counts', token),

  approve: (id: string, token: string) =>
    api.post<SettlementResponse>(`/api/admin/settlements/${id}/approve`, {}, token),

  hold: (id: string, reason: string, token: string) =>
    api.post<SettlementResponse>(`/api/admin/settlements/${id}/hold`, { reason }, token),

  release: (id: string, token: string) =>
    api.post<SettlementResponse>(`/api/admin/settlements/${id}/release`, {}, token),

  voidSettlement: (id: string, reason: string, token: string) =>
    api.post<SettlementResponse>(`/api/admin/settlements/${id}/void`, { reason, confirm: 'VOID' }, token),

  addAdjustment: (id: string, type: 'CREDIT' | 'DEBIT', amount: number, reason: string, token: string) =>
    api.post<SettlementResponse>(`/api/admin/settlements/${id}/adjustments`, { type, amount: String(amount), reason }, token),

  runCycle: (garageId: string, token: string) =>
    api.post<RunCycleResult>(`/api/admin/settlements/run-cycle?garageId=${garageId}`, {}, token),

  // Preview of what a manual run would wrap for one garage (settlement tab, garage detail).
  getEligibleOrders: (garageId: string, token: string) =>
    api.get<EligibleOrdersResponse>(`/api/settlements/eligible-orders?garageId=${garageId}`, token),

  // Garages with collectable orders right now ("Ready to settle" on the settlements screen).
  getCollectable: (token: string) =>
    api.get<CollectableGarage[]>('/api/admin/settlements/collectable', token),

  // Spec 053 Flow D1 — Exceptions tab (detector only; settling still goes through runCycle).
  // BR-12: ELIGIBLE orders whose eligible-at is older than 2 × the garage's cycle length.
  getOrphans: (token: string) =>
    api.get<OrphanedOrder[]>('/api/admin/settlements/exceptions/orphans', token),

  // BR-13 / AC-19 / AC-21d: open DEBIT adjustments with age; ≥60d PROVISION, ≥90d DECISION_REQUIRED.
  getClawbacks: (token: string) =>
    api.get<AgedClawback[]>('/api/admin/settlements/exceptions/clawbacks', token),

  // AC-20: open adjustments grouped by garage (type, source, amount, reason, age, refs).
  getOpenAdjustments: (token: string, garageId?: string) =>
    api.get<OpenAdjustmentGroup[]>(
      `/api/admin/settlements/adjustments?open=true${garageId ? `&garageId=${garageId}` : ''}`, token),
};

/** Spec 053 BR-12 — an ELIGIBLE order the collection rule should have wrapped but did not. */
export interface OrphanedOrder {
  orderId: string;
  orderNumber: string | null;
  garageId: string;
  garageName: string | null;
  eligibleAt: string;
  ageDays: number;
  /** Garage's settlement cycle (WEEKLY / BI_WEEKLY / MONTHLY) at detection time. */
  cycle: string | null;
  /** Residual cause hint (no active config, excluded and never re-admitted, …). */
  reasonHint?: string | null;
}

/** BR-13: ≥60 days open → PROVISION; AC-21d: ≥90 days → DECISION_REQUIRED (write off or block). */
export type ClawbackFlag = 'NONE' | 'PROVISION' | 'DECISION_REQUIRED';

/** Spec 053 AC-19 — an open DEBIT adjustment (refund clawback or carried balance) with its age. */
export interface AgedClawback {
  adjustmentId: string;
  garageId: string;
  garageName: string | null;
  amount: number;
  /** Where the debit came from, e.g. REFUND_CLAWBACK, CARRIED_BALANCE, MANUAL. */
  source?: string | null;
  createdAt: string;
  ageDays: number;
  flag: ClawbackFlag;
  refRefundId: string | null;
  refOrderId?: string | null;
  refOrderNumber: string | null;
}

/**
 * Links back to whatever originated an adjustment (refund / order / credit note / settlement).
 * Mirrors merchant-settlement `GarageAdjustmentsResponse.Refs` field-for-field.
 */
export interface AdjustmentRefs {
  refundId?: string | null;
  orderId?: string | null;
  orderNumber?: string | null;
  creditNoteId?: string | null;
  /** The settlement whose carried balance / clawback produced this adjustment (not the one collecting it). */
  originSettlementId?: string | null;
  originSettlementNumber?: string | null;
  faultParty?: string | null;
}

export interface OpenAdjustmentItem {
  id: string;
  type: 'CREDIT' | 'DEBIT';
  source: string | null;
  amount: number;
  reason: string | null;
  ageDays: number;
  refs?: AdjustmentRefs | null;
}

/** Spec 053 AC-20 — open adjustments grouped by garage. */
export interface OpenAdjustmentGroup {
  garageId: string;
  garageName: string | null;
  items: OpenAdjustmentItem[];
}

export interface EligibleOrderEntry {
  orderId: string;
  amount: number;
  paymentId: string | null;
  customerName: string | null;
  serviceSummary: string | null;
  commissionAmount: number | null;
  netAmount: number | null;
  completedAt: string | null;
  settlementEligibleAt: string | null;
  settlementState: string;
}

export interface EligibleOrdersResponse {
  garageId: string;
  orders: EligibleOrderEntry[];
}

export interface CollectableGarage {
  garageId: string;
  garageName: string | null;
  orderCount: number;
  grossTotal: number;
  netTotal: number;
  oldestCompletedAt: string | null;
  /**
   * #520 — the garage already has a non-void settlement at the period a run would close (yesterday,
   * Dubai), so "Run settlement now" would answer `PERIOD_ALREADY_SETTLED`. The orders are real; they
   * are collected by the next period's run. The panel must not call this row "ready".
   */
  periodAlreadySettled: boolean;
  existingSettlementNumber: string | null;
}

/**
 * #520 — `POST /api/admin/settlements/run-cycle`. Always a 202; `reason` is set exactly when
 * `generated` is false, so a no-op can be explained instead of looking like a success.
 */
export interface RunCycleResult {
  generated: boolean;
  settlementId: string;
  reason: 'PERIOD_ALREADY_SETTLED' | 'NO_ELIGIBLE_ORDERS' | null;
  existingSettlementNumber: string | null;
  periodEnd: string | null;
}

/** The notice the admin reads when a run produced nothing. */
export function runCycleReasonText(result: RunCycleResult): string {
  if (result.reason === 'PERIOD_ALREADY_SETTLED') {
    const number = result.existingSettlementNumber ?? 'one that already exists';
    return `A settlement for this period already exists: ${number}. Orders completed since then will be `
      + 'collected in the next period.';
  }
  return 'Nothing to settle — no orders past their hold window and no open adjustments.';
}

export interface SettlementCounts {
  needsAction: number;
  awaitingPayment: number;
  paid: number;
  onHold: number;
  voided: number;
  totalOutstanding: number;
}
